import CwApiClient, { Endpoints, HttpOptions } from 'services/CwApiClient';
import { AxiosResponse, Method } from 'axios';
import CwCache, { CacheVersions } from 'services/CwCache';
import { Config } from 'types/api/config';
import {
  CwResponse,
  parseSwimlanes,
  parseShowVideos,
  parseVideo,
  parseVideos,
  CwErrors,
  parseEpgChannels,
  Swimlanes,
  Lane,
  parseLiveEvent,
  parseLiveEvents,
  parseVideoPlatform,
  parseShowDetails,
  PromoLane,
  VideoLaneItem,
} from 'services/cwData';
import ApiServiceError from 'support/ApiServiceError';
import { EpgChannel, Season, Video } from 'types/api/media';
import { ErrorType } from 'types/analytics';
import { AppData } from '@lightningjs/sdk';
import NavBar from 'components/widgets/NavBar';
import AppLaunch from 'components/widgets/AppLaunch';
import { constants } from 'aliases';
import { ContentHubSlugs } from 'types/contentHubs';
import { getTivoApiScreens } from 'services/tivoService';
import { signJwt } from 'support/jwtUtils';

type RequestProps<TResult> = {
  method: Method;
  cacheKey: keyof CacheVersions;
  endpoint: string;
  preventCaching?: boolean;
  parser?: (response: unknown) => TResult;
  options?: HttpOptions;
};

type ResponseProps<TResult, TAxiosResponse> = {
  endpoint: string;
  timeout: number;
  response: TAxiosResponse;
  preventCaching?: boolean;
  parser?: (response: unknown) => TResult;
};

type FetchingLane = {
  index: number;
  lanes: Promise<(Lane | VideoLaneItem)[]>;
};

type FetchedLane = {
  index: number;
  lanes: (Lane | VideoLaneItem)[];
};

export const MAX_GUID_COUNT = 32;

const client = new CwApiClient();
const cache = new CwCache(client);

const sendRequest = async <TResult>({
  method,
  cacheKey,
  endpoint,
  preventCaching = false,
  parser,
  options,
}: RequestProps<TResult>): Promise<TResult> => {
  const cachedResponse = cache.getCachedResponse<TResult>(endpoint);
  if (cachedResponse) return cachedResponse;

  // Update timeouts if they haven't already been initialized
  if (
    !cache.areTimeoutsInitialized() &&
    !preventCaching &&
    cacheKey !== 'config'
  ) {
    await initializeCacheTimeouts();
  }
  const timeout = cache.getTimeout(cacheKey);

  const cacheVersion = await cache.getCacheVersion(cacheKey);

  const path = `${endpoint}cacheversion_${cacheVersion}/`;
  const response = await client.request<CwResponse>(method, path, options);

  return handleResponse<TResult, typeof response>({
    endpoint,
    timeout,
    response,
    preventCaching,
    parser,
  });
};

const handleResponse = <
  TResult,
  TAxiosResponse extends AxiosResponse<CwResponse>,
>({
  endpoint,
  timeout,
  response,
  preventCaching,
  parser,
}: ResponseProps<TResult, TAxiosResponse>): TResult => {
  if (response.data.result !== 'ok') {
    throw new ApiServiceError(
      response.data.msg === CwErrors.NOT_FOUND
        ? ErrorType.NOT_FOUND
        : ErrorType.MISSING_CONTENT,
    );
  }

  let result: TResult;
  if (parser) {
    try {
      // Attempt to parse the data received
      result = parser(response.data);
    } catch (e) {
      throw new ApiServiceError(ErrorType.MISSING_CONTENT);
    }
  } else {
    result = response.data as TResult;
  }

  if (!preventCaching) {
    cache.cacheResponse<TResult>(endpoint, result, timeout);
  }
  return result;
};

const createEndpoint = <T extends Record<string, string>>(
  endpoint: Endpoints,
  params: T = {} as T,
): string => {
  const { apiversion, channel } = constants.api;
  const device = AppData?.api.device ?? '';

  params = {
    ...params,
    apiversion,
    channel,
    device,
  };

  const paramsStr = Object.keys(params).reduce((prevStr, key) => {
    const value = params[key];
    return prevStr + `${key}_${value}/`;
  }, '');

  return `${endpoint}${paramsStr}`;
};

export const initializeCacheTimeouts = async () => {
  // Don't update timeouts if they have already been initialized
  if (cache.areTimeoutsInitialized()) return;

  const config = await getConfig();
  cache.updateTimeouts(config);
};

export const getConfig = async () => {
  const endpoint = createEndpoint(Endpoints.CONFIG);
  const config = await sendRequest<Config>({
    method: 'GET',
    cacheKey: 'config',
    endpoint,
  });

  // Update any properties that rely on the new config
  AppData!.privacyModifiedDate = config.privacy['modified_date'];
  AppData!.tivoEndpoints = config.endpoints;
  AppData!.ux = config.ux;
  AppData!.ayswIdleTimes = config.ux.aysw_minutes;
  AppData!.bookmarkHeartbeatIntervalSeconds =
    config.ux.bookmark_heartbeat_interval_seconds;
  AppData!.adParams = config['ad-params'];
  AppData!.privacy = config.privacy;
  AppData!.navigationItems = config.navigation.items;
  AppData!.topNavigationItems = config.navigation.top_items;
  AppData!.config = { login: config['login'] };
  AppData!.errorMessages = config.error_messages;

  const { drmService, contentSourceId } = parseVideoPlatform(config);
  AppData!.google.contentSourceId = contentSourceId;
  AppData!.drmService = drmService;

  NavBar.updateTabs();
  AppLaunch.termsUrl = config.privacy.terms_of_use_url;
  AppLaunch.promptData = config.privacy.launch_prompt;

  return config;
};

/**
 * Faux endpoint for the OneTrust cross-device JWT. It signs client side with
 * the private key from config until an endpoint is provided.
 */
export const getOneTrustJwt = async (uid: string): Promise<string> => {
  const config = await getConfig();
  const { key, key_id, expiration_time_secs } = config.privacy.one_trust ?? {};
  const expirationSecs = Number(expiration_time_secs);

  if (!key || !key_id || !isFinite(expirationSecs) || expirationSecs <= 0) {
    throw new Error('Missing privacy.one_trust configuration');
  }

  const iat = Math.floor(Date.now() / 1000);

  return signJwt({
    claims: { sub: uid, iat, exp: iat + expirationSecs },
    keyId: key_id,
    privateKey: key,
  });
};

export const getContentHub = async (hubSlug: string) => {
  const endpoint = createEndpoint(Endpoints.SWIMLANES, {
    type: hubSlug,
  });
  const swimlanes = await getSwimlanes(endpoint);
  if (!swimlanes) return null;

  const fetchingLanes = fetchTivoLanes(swimlanes);
  if (hubSlug === ContentHubSlugs.HOME && swimlanes.layout === 'carousels') {
    fetchingLanes.push(...fetchHistoryLanes(swimlanes));
  }
  if (!fetchingLanes.length) return swimlanes;

  const fetchingLanesPromise = fetchingLanes.map(
    fetchingLane => fetchingLane.lanes,
  );
  const newLanes = await Promise.allSettled(fetchingLanesPromise);
  if (!newLanes.length) return swimlanes;

  const fetchedLanes: FetchedLane[] = newLanes
    .map((lane, i) =>
      lane.status !== 'rejected'
        ? {
            index: fetchingLanes[i]!.index,
            lanes: lane.value,
          }
        : null,
    )
    .filter(lane => lane !== null);

  return updateLanes(swimlanes, fetchedLanes);
};

// update swimlanes with the multiple tivo api screens
const fetchTivoLanes = (swimlanes: Swimlanes): FetchingLane[] => {
  const fetchingLanes: FetchingLane[] = [];

  swimlanes.lanes.forEach((lane, index) => {
    if (lane.laneType === 'tivo-api-screens') {
      fetchingLanes.push({
        index,
        lanes: getTivoApiScreens(lane.apiUrl),
      });
    }
  });

  return fetchingLanes;
};

const fetchHistoryLanes = (swimlanes: Swimlanes): FetchingLane[] => {
  const fetchingLanes: FetchingLane[] = [];

  swimlanes.lanes.forEach((lane, index) => {
    if (lane.laneType === 'continue-watching') {
      fetchingLanes.push({
        index,
        lanes: AppData!.historyService.getContinueLaneItems(
          constants.watchHistory.lanePageSize,
        ),
      });
    }
  });

  return fetchingLanes;
};

const updateLanes = (swimlanes: Swimlanes, fetchedLanes: FetchedLane[]) => {
  const updatedLanes: (Lane | PromoLane)[] = [];

  // re-build lanes object to include possible tivo and continue-watching lanes
  swimlanes.lanes.forEach((lane, index) => {
    const fetchedLaneIndex = fetchedLanes.findIndex(
      fetchedLane => fetchedLane.index === index,
    );
    if (fetchedLaneIndex < 0) {
      updatedLanes.push(lane as Lane);
      return;
    }
    if (lane.laneType === 'tivo-api-screens') {
      const tivoApiScreens = fetchedLanes[fetchedLaneIndex]!.lanes;
      const screens = (tivoApiScreens as Lane[])
        .map(screen => {
          if (!screen.items.length) return null;
          screen.presentation = lane.presentation;
          screen.slug = lane.slug;
          return screen;
        })
        .filter(screen => !!screen) as Lane[];

      updatedLanes.push(...screens);
    } else if (lane.laneType === 'continue-watching') {
      const continueWatchItems = fetchedLanes[fetchedLaneIndex]!
        .lanes as VideoLaneItem[];
      if (continueWatchItems.length) lane.items = continueWatchItems;

      updatedLanes.push(lane as Lane);
    } else {
      updatedLanes.push(lane as Lane);
    }
  });

  swimlanes.lanes = updatedLanes;
  swimlanes.lanesCount = updatedLanes.length;
  return swimlanes;
};

const getSwimlanes = async (endpoint: string) => {
  return await sendRequest({
    method: 'GET',
    cacheKey: 'swimlanes',
    endpoint,
    parser: parseSwimlanes,
  });
};

export const getShowByShowSlug = async (slug: string) => {
  const endpoint = createEndpoint(Endpoints.SHOWS, { show: slug });
  const showResponse = await sendRequest({
    method: 'GET',
    cacheKey: 'shows',
    endpoint,
    parser: parseShowDetails,
  });

  if (showResponse?.showSlug !== slug) {
    throw new ApiServiceError(ErrorType.MISSING_CONTENT);
  }

  return showResponse;
};

export const getEpisodeListByShowSlug = async (
  slug: string,
  season: Season,
) => {
  const endpoint = createEndpoint(Endpoints.VIDEOS, {
    show: slug,
    type: 'episodes',
    season: season.code,
  });

  const showVideos = await sendRequest({
    method: 'GET',
    cacheKey: 'videos',
    endpoint,
    parser: parseShowVideos,
  });
  showVideos.season = season;
  return showVideos;
};

export const getExtrasListByShowSlug = async (slug: string) => {
  const endpoint = createEndpoint(Endpoints.VIDEOS, {
    show: slug,
    type: 'extras',
  });
  return await sendRequest({
    method: 'GET',
    cacheKey: 'videos',
    endpoint,
    parser: parseShowVideos,
  });
};

export const getVideo = async (guid: string) => {
  const endpoint = createEndpoint(Endpoints.VIDEO_METADATA, { guid });
  return await sendRequest({
    method: 'GET',
    cacheKey: 'videos',
    endpoint,
    parser: parseVideo,
  });
};

/**
 * Requests video data for multiple GUIDs at once
 *
 * @param guids List of GUIDs. If more than 32 are provided, it will batch request.
 * @returns The video data for every GUID provided
 */
export const getVideos = async (guids: string[]) => {
  const videos: Array<Video> = [];
  const requests: Promise<Video[]>[] = [];

  // Loop to create chunks of media identifiers that are MAX_GUID_COUNT sized.
  // This ensures we don't exceed the GUID limit for videos
  for (let left = 0; left < guids.length; left += MAX_GUID_COUNT) {
    const right = Math.min(left + MAX_GUID_COUNT, guids.length);
    const chunk = guids.slice(left, right);

    // create string of up to 32 video GUIDs split with a comma
    const batch = chunk.reduce((prevStr, value) => {
      return prevStr ? `${prevStr},${value}` : value;
    }, '');

    const endpoint = createEndpoint(Endpoints.VIDEOS_METADATA, { guid: batch });
    requests.push(
      sendRequest({
        method: 'GET',
        cacheKey: 'videos',
        endpoint,
        preventCaching: true, // GUIDs are fairly unique so we prevent caching
        parser: parseVideos,
      }),
    );
  }

  (await Promise.allSettled(requests)).forEach(result => {
    if (result.status === 'fulfilled')
      result.value.forEach(video => videos.push(video));
  });

  return videos;
};

export const getLiveEvent = async (slug: string) => {
  const endpoint = createEndpoint(Endpoints.LIVE_EVENT, { event: slug });
  return await sendRequest({
    method: 'GET',
    cacheKey: 'shows',
    endpoint,
    parser: parseLiveEvent,
  });
};

export const getAllLiveEvents = async () => {
  const endpoint = createEndpoint(Endpoints.LIVE_EVENT, {
    event: 'list-all-events-details-page',
  });
  return await sendRequest({
    method: 'GET',
    cacheKey: 'shows',
    endpoint,
    parser: parseLiveEvents,
  });
};

export const getEpgPage = async (pageNum: number, pageSize: number) => {
  const endpoint = createEndpoint(Endpoints.EPG, {
    page: pageNum.toString(),
    pagesize: pageSize.toString(),
  });
  return await sendRequest<{ channels: EpgChannel[]; maxChannels: number }>({
    method: 'GET',
    cacheKey: 'epg',
    endpoint,
    preventCaching: true,
    parser: parseEpgChannels,
  });
};

export const getEpgChannelBySlug = async (slug: string) => {
  const endpoint = createEndpoint(Endpoints.EPG, { epgchannel: slug });
  return await sendRequest({
    method: 'GET',
    cacheKey: 'epg',
    endpoint,
    parser: parseEpgChannels,
  });
};

export const getSettingsText = async (
  textFor: 'TERMS_OF_USE' | 'PRIVACY_POLICY' | 'NIELSEN_MEASUREMENT',
) => {
  const endpoint = createEndpoint(Endpoints[textFor]);

  const parser = (data: any): string => data.content;
  return await sendRequest({
    method: 'GET',
    cacheKey: 'config',
    endpoint,
    preventCaching: true,
    parser,
  });
};

export const useCacheBackup = async () => {
  await cache.useCacheBackup();
};
